import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import theme from 'theme'

/* Utils */
import { AnalysisDataTable, ClusterRecord, IntegrationInstance, OperatingSystem } from '../../../models'
import useCoverageAnalyzerData from './useCoverageAnalyzerData'

/* Components */
import { Container, Wrapper } from './coverageAnalyzer.styles'
import CoverageLoading from './CoverageLoading'
import { breadcrumbs } from '../../../lib/utils'
import HeaderComponent from '../../components/header/HeaderComponent'
import ToolbarComponent from '../../components/header/ToolbarComponent'
import ContentWrapper from '../../components/header/ContentWrapper'
import ToolbarList from './components/ToolbarList'
import TabsHeader from '../../components/tabs-header'
import TabPanel from '../cpi-details/components/LibraryDetailsTabPanel'
import CoverageTrendsTab from './sections/trends-tab'
import Analysis from './components/Analysis'
import CoverageAnalyzerInitial from './CoverageAnalyzerInitial'
import { ToolbarWrapper } from './components/ToolbarList.styles'
import { mapAnalysisData, mapClusterData } from './utils'

const CoverageAnalyzer: FC = () => {
  const [tabValue, setTabValue] = useState(0)
  const [analyzed, setAnalyzed] = useState(false)
  const [search, setSearch] = useState('')
  const [showFilter, setShowFilter] = useState(true)
  const [isFilterActive, setIsFilterActive] = useState(false)
  const [isTrendsFilterActive, setIsTrendsFilterActive] = useState(false)

  const {
    firstSource,
    secondSource,
    handleChangeSource,
    selectedOperatingSystem,
    setSelectedOperatingSystem,
    selectedSeenWithin,
    setSelectedSeenWithin,
    setSelectedTimePeriod,
    sourceChanged,
    selectedTimePeriod,
    chartData,
    fetchTrendData,
    chartLoading,
    setChartLoading,
    instances,
    optionsLoading,
    analysisData,
    fetchAnalysisData,
    loadingAnalysisData,
    setLoadingAnalysisData,
    selectedInstances,
    pagination,
    setPagination,
    tableFilters,
    setTableFilters,
    firstInstanceAssets,
    secondInstanceAssets,
    supportedIntegrations,
    initialLoad,
    fetchTotalAssets,
    setFirstSource,
    setSecondSource,
    lastActiveSeenWithin,
    setLastActiveSeenWithin,
    setSourceChanged,
    selectedOsTrends,
    setSelectedOsTrends,
    exporting,
    handleExport,
    setActiveSeenWithin,
    activeSeenWithin,
    isCompareToAll,
    analyzedCompareToAll,
    analyzedTrendMode
  } = useCoverageAnalyzerData()

  const getInstanceName = useCallback((instance: IntegrationInstance | undefined) => {
    if (!instance) return ''

    const { integration_name: integrationName, id } = instance
    const idPart = id.split('-')[0]

    return `${integrationName}_${idPart}`
  }, [])

  const firstInstance = useMemo(
    () =>
      instances && selectedInstances.length > 0 ? instances.find((n) => n.id === selectedInstances[0].id) : undefined,
    [instances, selectedInstances]
  )
  const secondInstance = useMemo(
    () =>
      instances && selectedInstances.length > 1 ? instances.find((n) => n.id === selectedInstances[1].id) : undefined,
    [instances, selectedInstances]
  )
  const firstInstanceName = useMemo(() => getInstanceName(firstInstance), [firstInstance, instances, selectedInstances])
  const secondInstanceName = useMemo(
    () => getInstanceName(secondInstance),
    [secondInstance, instances, selectedInstances]
  )

  const isV2 = Boolean(firstInstance?.is_v2 && secondInstance?.is_v2)
  /* Render off the committed (analyzed) mode, not the live dropdown — picking "All" must not reshape
   * the table until Analyze runs. */
  const data: AnalysisDataTable[] = analyzedCompareToAll
    ? mapClusterData(analysisData as unknown as ClusterRecord[])
    : mapAnalysisData(
        isV2,
        analysisData,
        isV2 ? firstInstance?.instance_name || '' : firstInstanceName,
        isV2 ? secondInstance?.instance_name || '' : secondInstanceName
      )
  const [rows, setRows] = useState<AnalysisDataTable[]>(data)

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    if (analysisData.length > 0 || chartData) {
      setTabValue(newValue)
    }
  }

  useEffect(() => {
    const isV2Effect = Boolean(firstInstance?.is_v2 && secondInstance?.is_v2)
    const mappedRows = analyzedCompareToAll
      ? mapClusterData(analysisData as unknown as ClusterRecord[])
      : mapAnalysisData(
          isV2Effect,
          analysisData,
          isV2Effect ? firstInstance?.instance_name || '' : firstInstanceName,
          isV2Effect ? secondInstance?.instance_name || '' : secondInstanceName
        )
    setRows(mappedRows)
    setLoadingAnalysisData(false)
  }, [analysisData, analyzedCompareToAll])

  const handleCloseBackdrop = () => {
    setChartLoading(false)
  }

  const handleAnalyzed = () => {
    setLoadingAnalysisData(true)

    setTimeout(() => {
      setAnalyzed(true)
    }, 150)
  }

  const handleSearch = useCallback(
    async ({
      integrationIds,
      searchTerm,
      filters,
      operatingSystem
    }: {
      integrationIds: string[]
      searchTerm: string
      filters: boolean[]
      operatingSystem: OperatingSystem
    }) => {
      await fetchAnalysisData({ integrationIds, searchTerm, filters, operatingSystem })
    },
    [fetchAnalysisData]
  )

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (args: {
          integrationIds: string[]
          searchTerm: string
          filters: boolean[]
          operatingSystem: OperatingSystem
        }) => handleSearchRef.current(args),
        300
      ),
    []
  )

  useEffect(() => {
    setLoadingAnalysisData(true)
    debouncedSearch({
      integrationIds: [firstSource, secondSource],
      searchTerm: search,
      filters: tableFilters,
      operatingSystem: selectedOperatingSystem
    })
  }, [search])

  if (!analyzed) {
    return (
      <Container className="aca-container-initial">
        <HeaderComponent breadcrumbs={breadcrumbs.ACA} noBg={true} borderBottom={true} />

        <CoverageAnalyzerInitial
          firstSource={firstSource}
          secondSource={secondSource}
          handleChangeSource={handleChangeSource}
          selectedSeenWithin={selectedSeenWithin}
          setSelectedSeenWithin={setSelectedSeenWithin}
          fetchTrendData={fetchTrendData}
          instances={instances}
          optionsLoading={optionsLoading}
          fetchAnalysisData={fetchAnalysisData}
          handleAnalyzed={handleAnalyzed}
          supportedIntegrations={supportedIntegrations}
          lastActiveSeenWithin={lastActiveSeenWithin}
          setLastActiveSeenWithin={setLastActiveSeenWithin}
          setActiveSeenWithin={setActiveSeenWithin}
        />
      </Container>
    )
  }
  return (
    <Container className="aca-container">
      {chartLoading && tabValue === 1 ? <CoverageLoading open={chartLoading} handleClose={handleCloseBackdrop} /> : ''}

      <HeaderComponent
        breadcrumbs={breadcrumbs.ACA}
        noBg={true}
        sx={{ borderBottom: `1px solid ${theme.colors.borderLow} !important` }}
      />

      <ToolbarComponent>
        <ToolbarWrapper className="controls-toolbar-wrapper-main">
          <ToolbarList
            firstSource={firstSource}
            setActiveSeenWithin={setActiveSeenWithin}
            secondSource={secondSource}
            setChartLoading={setChartLoading}
            handleChangeSource={handleChangeSource}
            selectedSeenWithin={selectedSeenWithin}
            setSelectedSeenWithin={setSelectedSeenWithin}
            fetchTrendData={fetchTrendData}
            setIsFilterActive={setIsFilterActive}
            setSelectedOperatingSystem={setSelectedOperatingSystem}
            instances={instances}
            optionsLoading={optionsLoading}
            fetchAnalysisData={fetchAnalysisData}
            handleAnalyzed={handleAnalyzed}
            mainToolbarList
            setTableFilters={setTableFilters}
            firstInstanceAssets={firstInstanceAssets}
            secondInstanceAssets={secondInstanceAssets}
            supportedIntegrations={supportedIntegrations}
            loadingAnalysisData={loadingAnalysisData}
            sourceChanged={sourceChanged}
            lastActiveSeenWithin={lastActiveSeenWithin}
            setLastActiveSeenWithin={setLastActiveSeenWithin}
            setSourceChanged={setSourceChanged}
            analysisData={analysisData}
            selectedInstances={selectedInstances}
            setIsTrendsFilterActive={setIsTrendsFilterActive}
            setSelectedOsTrends={setSelectedOsTrends}
            setSelectedTimePeriod={setSelectedTimePeriod}
            fieldWidth="300"
            setSearch={setSearch}
            isCompareToAll={isCompareToAll}
          />
        </ToolbarWrapper>
      </ToolbarComponent>

      <TabsHeader
        tabValue={tabValue}
        handleTabChange={handleTabChange}
        title="Asset Coverage Analyzer"
        tabLabels={['Assets', 'Coverage Trends']}
        loading={false}
        sx={{ marginTop: '24px !important' }}
      />

      <ContentWrapper
        line={2}
        marginTop={24}
        normalHeight={
          (!analysisData.length && !chartData) || tabValue === 1 ? 'false' : loadingAnalysisData ? 'true' : 'false'
        }
        paddingBottom={0}
        scrollHeight={analysisData.length > 0 || chartData ? 250 : 200}
        sx={{ overflow: 'hidden !important' }}
      >
        <Wrapper className="wrapper">
          <TabPanel value={tabValue} index={0}>
            <Analysis
              rows={rows}
              setRows={setRows}
              handleExport={handleExport}
              exporting={exporting}
              firstSourceInField={firstSource}
              secondSourceInField={secondSource}
              selectedSeenWithin={selectedSeenWithin}
              selectedOperatingSystem={selectedOperatingSystem}
              setSelectedOperatingSystem={setSelectedOperatingSystem}
              fetchAnalysisData={fetchAnalysisData}
              loadingAnalysisData={loadingAnalysisData}
              pagination={pagination}
              setPagination={setPagination}
              tableFilters={tableFilters}
              setTableFilters={setTableFilters}
              setLoadingAnalysisData={setLoadingAnalysisData}
              initialLoad={initialLoad}
              search={search}
              setSearch={setSearch}
              fetchTotalAssets={fetchTotalAssets}
              firstInstance={firstInstance}
              secondInstance={secondInstance}
              showFilter={showFilter}
              isFilterActive={isFilterActive}
              setIsFilterActive={setIsFilterActive}
              setShowFilter={setShowFilter}
              setFirstSource={setFirstSource}
              setSecondSource={setSecondSource}
              selectedInstances={selectedInstances}
              activeSeenWithin={activeSeenWithin}
              setSelectedSeenWithin={setSelectedSeenWithin}
              isCompareToAll={isCompareToAll}
              analyzedCompareToAll={analyzedCompareToAll}
              instances={instances}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <CoverageTrendsTab
              firstSource={firstSource}
              secondSource={secondSource}
              selectedOperatingSystem={selectedOsTrends}
              setSelectedOperatingSystem={setSelectedOsTrends}
              chartData={chartData}
              selectedTimePeriod={selectedTimePeriod}
              setSelectedTimePeriod={setSelectedTimePeriod}
              fetchTrendData={fetchTrendData}
              instances={instances}
              selectedInstances={selectedInstances}
              isTrendsFilterActive={isTrendsFilterActive}
              setIsTrendsFilterActive={setIsTrendsFilterActive}
              chartLoading={chartLoading}
              isCompareToAll={analyzedTrendMode.isCompareToAll}
              sentinelInSourceA={analyzedTrendMode.sentinelInSourceA}
            />
          </TabPanel>
        </Wrapper>
      </ContentWrapper>
    </Container>
  )
}

export default CoverageAnalyzer
